import { createSlice } from '@reduxjs/toolkit';
import {
  getPortalBasicSettings,
  updatePortalBasicSettings,
  getPortalConfigurations,
  updatePortalConfigurations,
  getPortalRulesSummary,
  getPortalStarringRules,
  getRule,
  getPropertiesForPage,
  createRule,
  deleteRule,
  getPropIdsMatchingCriteria,
  updateStarringRulesOrder,
  getAttributesForCriteria,
  getSelectOptions,
  getDevelopmentTypes,
  patchRule,
  searchLocationDivisions,
  getCustomTagsSearch,
  requestSmartPublishingRulesUpgrade,
  getUpgradePending,
  getDevelopmentsForPage,
  addXmlFile,
  deleteXmlFile,
  getXmlFiles,
  getImportedFromOrigins,
  getXmlFileHistory,
} from './services';
import { fetchPropertyTypes } from '@/store/slices/components/webmanager/services';
import { ADDRESS_VISIBILITY_CONFIG_KEY } from '@/components/portals/publishingRules/PortalsTabsMenu/BasicSettings/AddressVisibility/constants';
import initialState from './initialState';

const applyPublicationConfigValue = (state, config, value) => {
  const current = state.portalData?.general_config?.[config];
  if (current) {
    current.value = value;
  }
  if (config === ADDRESS_VISIBILITY_CONFIG_KEY) {
    state.portalData.address_visibility_level = Number(value);
  }
};

const setLabels = (rulesList) => {
  let firstActiveFound = false;

  return rulesList.map((rule) => {
    let labelKey = 'InactiveRule';

    if (rule.active) {
      if (!firstActiveFound) {
        labelKey = 'FirstRuleToCheck';
        firstActiveFound = true;
      } else {
        labelKey = 'NextRuleToCheck';
      }
    }

    rule.label = labelKey;
    return rule;
  });
};

const updateRuleInArray = (rulesList, ruleUpdated) =>
  rulesList.map((rule) => {
    if (rule.id !== ruleUpdated.id) return rule;
    return { ...ruleUpdated };
  });

const removeRuleFromArray = (rulesList, ruleIdToRemove) =>
  rulesList.filter((rule) => rule.id !== ruleIdToRemove);

const formatProps = (props_payload = []) =>
  props_payload.map((prop) => {
    prop.image = prop.picture_url;
    prop.title = prop.address;
    prop.name = prop.publication_title;
    prop.type = 'Property';
    const statusText = prop.status?.name || prop.status || prop.status_id;
    prop.status = String(statusText || '');
    return prop;
  });

const formatDevelopments = (devs_payload = []) =>
  devs_payload.map((dev) => {
    dev.image = dev.picture_url;
    dev.title = dev.address || dev.name;
    dev.name = dev.name || '';
    dev.type = dev.type || '';
    dev.location = dev.location || '';
    dev.typeSelected = 'development';

    return dev;
  });

export const PortalSlice = createSlice({
  name: 'portal',
  initialState,
  reducers: {
    setGeneralConfig(state, action) {
      const { key, value } = action.payload;
      applyPublicationConfigValue(state, key, value);
    },
    updatePublicationLimit: (state, action) => {
      const { star, branch, value } = action.payload;
      const starConfig = state.portalConfigurations?.star_config?.[star]?.conf;

      if (starConfig) {
        starConfig[branch] = value ? Number(value) : null;
      }
    },
    setStarringRulesShowInactive: (state, action) => {
      state.starringRulesShowInactive = action.payload;
    },
    setStarringRuleFormData: (state, action) => {
      const data = action.payload;
      if (typeof data === 'function') {
        state.starringRuleForm = data(state.starringRuleForm);
      } else {
        state.starringRuleForm = {
          ...state.starringRuleForm,
          ...data,
        };
      }
    },
    resetStarringRuleForm: (state) => {
      state.starringRuleForm = {
        ...initialState.starringRuleForm,
        attributeValidationErrors: {},
      };
    },
    clearEditingRuleData: (state) => {
      state.editingRuleData = null;
    },
    updateStarringRule: (state, action) => {
      state.starringRules = setLabels(
        updateRuleInArray(state.starringRules, action.payload)
      );
      state.activeStarringRulesCount = state.starringRules.filter(
        (rule) => rule.active
      ).length;
      // Update portal rules summary actives count to keep it in sync
      if (state.portalRulesSummary) {
        state.portalRulesSummary.actives = state.activeStarringRulesCount;
      }
    },
    setPropsMatchingCriteriaToNone: (state) => {
      state.totalPropsMatchingCriteria = 0;
      state.totalPropsMatchingNotApplying = 0;
      state.propsIdsMatchingCriteria = [];
    },
    setAttributeValidationError: (state, action) => {
      const { attributeId, hasError } = action.payload;
      if (hasError) {
        state.starringRuleForm.attributeValidationErrors[attributeId] = true;
      } else {
        delete state.starringRuleForm.attributeValidationErrors[attributeId];
      }
      // Update global validation state based on remaining errors
      state.starringRuleForm.hasValidationErrors =
        Object.keys(state.starringRuleForm.attributeValidationErrors).length >
        0;
    },
    clearAttributeValidationErrors: (state, action) => {
      const { attributeIds } = action.payload;
      attributeIds.forEach((id) => {
        delete state.starringRuleForm.attributeValidationErrors[id];
      });
      // Update global validation state based on remaining errors
      state.starringRuleForm.hasValidationErrors =
        Object.keys(state.starringRuleForm.attributeValidationErrors).length >
        0;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    switchToBasicSettings: (state) => {
      state.activeTab = 'basicSettings';
    },
    clearXmlFileHistory: (state) => {
      state.xmlFileHistory = [];
      state.errorGetXmlFileHistory = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPortalBasicSettings.pending, (state) => {
        state.loadingGetPortalBasicSettings = true;
        state.errorGetPortalBasicSettings = false;
      })
      .addCase(getPortalBasicSettings.fulfilled, (state, action) => {
        state.portalData = action.payload;
        state.loadingGetPortalBasicSettings = false;
        state.errorGetPortalBasicSettings = false;
      })
      .addCase(getPortalBasicSettings.rejected, (state) => {
        state.loadingGetPortalBasicSettings = false;
        state.errorGetPortalBasicSettings = true;
      })
      .addCase(updatePortalBasicSettings.pending, (state) => {
        state.loadingUpdateConfig = true;
        state.errorUpdateConfig = false;
      })
      .addCase(updatePortalBasicSettings.fulfilled, (state, action) => {
        const { config, value } = action.payload;
        applyPublicationConfigValue(state, config, value);
        state.loadingUpdateConfig = false;
        state.errorUpdateConfig = false;
      })
      .addCase(updatePortalBasicSettings.rejected, (state) => {
        state.loadingUpdateConfig = false;
        state.errorUpdateConfig = true;
      })
      .addCase(getPortalConfigurations.pending, (state) => {
        state.loadingGetPortalConfigurations = true;
        state.errorGetPortalConfigurations = false;
      })
      .addCase(getPortalConfigurations.fulfilled, (state, action) => {
        const { subportalId } = action.meta.arg;

        state.portalConfigutations = action.payload;

        if (subportalId) {
          state.portalConfigutations.subportal_id = Number(subportalId);
        }

        state.loadingGetPortalConfigurations = false;
        state.errorGetPortalConfigurations = false;
      })
      .addCase(getPortalConfigurations.rejected, (state) => {
        state.loadingGetPortalConfigurations = false;
        state.errorGetPortalConfigurations = true;
      })
      .addCase(updatePortalConfigurations.pending, (state) => {
        state.loadingUpdatePortalConfigurations = true;
        state.errorUpdatePortalConfigurations = false;
      })
      .addCase(updatePortalConfigurations.fulfilled, (state, action) => {
        const { parameters } = action.meta.arg;

        parameters.forEach(({ star, branch, value }) => {
          if (state.portalConfigutations?.star_config?.[star]?.conf) {
            state.portalConfigutations.star_config[star].conf[branch] =
              value === '' ? null : Number(value);
          }
        });

        state.loadingUpdatePortalConfigurations = false;
        state.errorUpdatePortalConfigurations = false;
      })
      .addCase(updatePortalConfigurations.rejected, (state) => {
        state.loadingUpdatePortalConfigurations = false;
        state.errorUpdatePortalConfigurations = true;
      })
      .addCase(getPortalRulesSummary.pending, (state) => {
        state.loadingGetPortalRulesSummary = true;
        state.errorGetPortalRulesSummary = false;
      })
      .addCase(getPortalRulesSummary.fulfilled, (state, action) => {
        state.portalRulesSummary = action.payload;
        state.isDevelopmentPortal = action.payload?.object_model === 'DEV';
        state.loadingGetPortalRulesSummary = false;
        state.errorGetPortalRulesSummary = false;
      })
      .addCase(getPortalRulesSummary.rejected, (state) => {
        state.loadingGetPortalRulesSummary = false;
        state.errorGetPortalRulesSummary = true;
      })
      .addCase(getPortalStarringRules.pending, (state) => {
        state.loadingGetStarringRules = true;
        state.errorGetStarringRules = false;
      })
      .addCase(getPortalStarringRules.fulfilled, (state, action) => {
        state.starringRules = setLabels(
          action.payload.results.filter((rule) => rule.type === 'starred')
        );
        state.activeStarringRulesCount = state.starringRules.filter(
          (rule) => rule.active
        ).length;
        state.loadingGetStarringRules = false;
        state.errorGetStarringRules = false;
      })
      .addCase(getPortalStarringRules.rejected, (state) => {
        state.loadingGetStarringRules = false;
        state.errorGetStarringRules = true;
      })
      .addCase(getRule.pending, (state) => {
        state.loadingGetRule = true;
        state.errorGetRule = false;
      })
      .addCase(getRule.fulfilled, (state, action) => {
        state.editingRuleData = action.payload;
        state.loadingGetRule = false;
        state.errorGetRule = false;
      })
      .addCase(getRule.rejected, (state) => {
        state.loadingGetRule = false;
        state.errorGetRule = true;
      })
      .addCase(createRule.pending, (state) => {
        state.loadingCreateRule = true;
        state.errorCreateRule = false;
      })
      .addCase(createRule.fulfilled, (state) => {
        // Increment total count when a rule is created
        if (state.portalRulesSummary) {
          state.portalRulesSummary.total =
            (state.portalRulesSummary.total || 0) + 1;
          state.portalRulesSummary.actives += 1;
        }
        // Increment active starring rules count (new rules are created as active by default)
        state.activeStarringRulesCount += 1;
        state.loadingCreateRule = false;
        state.errorCreateRule = false;
      })
      .addCase(createRule.rejected, (state) => {
        state.loadingCreateRule = false;
        state.errorCreateRule = true;
      })
      .addCase(patchRule.pending, (state) => {
        state.loadingPatchRule = true;
        state.errorPatchRule = false;
      })
      .addCase(patchRule.fulfilled, (state) => {
        state.loadingPatchRule = false;
        state.errorPatchRule = false;
      })
      .addCase(patchRule.rejected, (state) => {
        state.loadingPatchRule = false;
        state.errorPatchRule = true;
      })
      .addCase(deleteRule.pending, (state) => {
        state.loadingDeleteRule = true;
        state.errorDeleteRule = false;
      })
      .addCase(deleteRule.fulfilled, (state, action) => {
        const deletedRuleId = action.meta.arg.ruleId;

        state.starringRules = setLabels(
          removeRuleFromArray(state.starringRules, deletedRuleId)
        );

        // Update active starring rules count based on remaining rules
        state.activeStarringRulesCount = state.starringRules.filter(
          (rule) => rule.active
        ).length;
        state.loadingDeleteRule = false;
        state.errorDeleteRule = false;
      })
      .addCase(deleteRule.rejected, (state) => {
        state.loadingDeleteRule = false;
        state.errorDeleteRule = true;
      })
      .addCase(fetchPropertyTypes.pending, (state) => {
        state.loadingFetchPropertyTypes = true;
      })
      .addCase(fetchPropertyTypes.fulfilled, (state, action) => {
        state.propertyTypes = action.payload;
        state.loadingFetchPropertyTypes = false;
      })
      .addCase(fetchPropertyTypes.rejected, (state, action) => {
        state.errorFetchPropertyTypes = action.error.message;
        state.loadingFetchPropertyTypes = false;
      })
      .addCase(getPropIdsMatchingCriteria.pending, (state, action) => {
        state.loadingPropIdsMatchingCriteria = true;
        state.errorPropIdsMatchingCriteria = false;
        // Track the latest in-flight request so out-of-order responses can be discarded
        state.propIdsMatchingCriteriaRequestId = action.meta.requestId;
      })
      .addCase(getPropIdsMatchingCriteria.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.propIdsMatchingCriteriaRequestId) {
          return;
        }

        state.currentAffectedProperties = [];

        // search-publishables can return properties and/or developments
        const properties = action.payload.properties || [];
        const developments = action.payload.developments || [];

        // Persist whichever IDs are relevant; consumer will decide which thunk to call next
        state.propsIdsMatchingCriteria =
          properties.length > 0 ? properties : developments;

        state.totalPropsMatchingCriteria = action.payload.count || 0;
        state.totalPropsMatchingNotApplying =
          action.payload.amount_not_modify || 0;
        state.loadingPropIdsMatchingCriteria = false;
        state.errorPropIdsMatchingCriteria = false;
      })
      .addCase(getPropIdsMatchingCriteria.rejected, (state, action) => {
        if (action.meta.requestId !== state.propIdsMatchingCriteriaRequestId) {
          return;
        }

        state.loadingPropIdsMatchingCriteria = false;
        state.errorPropIdsMatchingCriteria = true;
      })

      .addCase(getPropertiesForPage.pending, (state) => {
        state.loadingAffectedProperties = true;
        state.errorAffectedProperties = false;
      })
      .addCase(getPropertiesForPage.fulfilled, (state, action) => {
        const formattedProps = formatProps(action.payload.properties);
        state.currentAffectedProperties = formattedProps;
        state.loadingAffectedProperties = false;
        state.errorAffectedProperties = false;
      })
      .addCase(getPropertiesForPage.rejected, (state) => {
        state.loadingAffectedProperties = false;
        state.errorAffectedProperties = true;
      })

      .addCase(getDevelopmentsForPage.pending, (state) => {
        state.loadingAffectedProperties = true;
        state.errorAffectedProperties = false;
      })
      .addCase(getDevelopmentsForPage.fulfilled, (state, action) => {
        const formatted = formatDevelopments(action.payload.developments);
        state.currentAffectedProperties = formatted;
        state.loadingAffectedProperties = false;
        state.errorAffectedProperties = false;
      })
      .addCase(getDevelopmentsForPage.rejected, (state) => {
        state.loadingAffectedProperties = false;
        state.errorAffectedProperties = true;
      })

      .addCase(updateStarringRulesOrder.pending, (state) => {
        state.updatingRules = true;
        state.errorUpdatingRules = false;
      })
      .addCase(updateStarringRulesOrder.fulfilled, (state, action) => {
        const { idsArray } = action.meta.arg;
        const reorderedRules = idsArray.map((ruleId) =>
          state.starringRules.find((rule) => rule.id === ruleId)
        );
        state.starringRules = setLabels(reorderedRules);
        state.updatingRules = false;
        state.errorUpdatingRules = false;
      })
      .addCase(updateStarringRulesOrder.rejected, (state) => {
        state.updatingRules = false;
        state.errorUpdatingRules = true;
      })
      .addCase(getAttributesForCriteria.pending, (state) => {
        state.loadingGetAttributes = true;
        state.errorGetAttributes = false;
      })
      .addCase(getAttributesForCriteria.fulfilled, (state, action) => {
        state.attributes = action.payload.objects || [];
        state.loadingGetAttributes = false;
        state.errorGetAttributes = false;
      })
      .addCase(getAttributesForCriteria.rejected, (state) => {
        state.loadingGetAttributes = false;
        state.errorGetAttributes = true;
      })
      .addCase(getSelectOptions.pending, (state, action) => {
        const { attributeId } = action.meta.arg;
        state.loadingSelectOptions[attributeId] = true;
      })
      .addCase(getSelectOptions.fulfilled, (state, action) => {
        const { attributeId, options } = action.payload;
        state.selectOptions[attributeId] = options;
        state.loadingSelectOptions[attributeId] = false;
      })
      .addCase(getSelectOptions.rejected, (state, action) => {
        const { attributeId } = action.meta.arg;
        state.loadingSelectOptions[attributeId] = false;
      })
      .addCase(getDevelopmentTypes.pending, (state) => {
        state.loadingGetDevelopmentTypes = true;
        state.errorGetDevelopmentTypes = false;
      })
      .addCase(getDevelopmentTypes.fulfilled, (state, action) => {
        state.developmentTypes = action.payload;
        state.loadingGetDevelopmentTypes = false;
        state.errorGetDevelopmentTypes = false;
      })
      .addCase(getDevelopmentTypes.rejected, (state) => {
        state.loadingGetDevelopmentTypes = false;
        state.errorGetDevelopmentTypes = true;
      })
      .addCase(searchLocationDivisions.pending, (state) => {
        state.loadingLocationSearch = true;
        state.errorLocationSearch = false;
      })
      .addCase(searchLocationDivisions.fulfilled, (state, action) => {
        state.locationSearchResults = action.payload;
        state.loadingLocationSearch = false;
        state.errorLocationSearch = false;
      })
      .addCase(searchLocationDivisions.rejected, (state) => {
        state.loadingLocationSearch = false;
        state.errorLocationSearch = true;
      })
      .addCase(getCustomTagsSearch.pending, (state) => {
        state.loadingCustomTags = true;
        state.errorCustomTags = false;
      })
      .addCase(getCustomTagsSearch.fulfilled, (state, action) => {
        state.customTagsData = action.payload;
        state.loadingCustomTags = false;
        state.errorCustomTags = false;
      })
      .addCase(getCustomTagsSearch.rejected, (state) => {
        state.loadingCustomTags = false;
        state.errorCustomTags = true;
      })
      .addCase(requestSmartPublishingRulesUpgrade.pending, (state) => {
        state.loadingRequestUpgrade = true;
        state.errorRequestUpgrade = false;
      })
      .addCase(requestSmartPublishingRulesUpgrade.fulfilled, (state) => {
        state.upgradePending = true;
        state.loadingRequestUpgrade = false;
        state.errorRequestUpgrade = false;
      })
      .addCase(requestSmartPublishingRulesUpgrade.rejected, (state) => {
        state.loadingRequestUpgrade = false;
        state.errorRequestUpgrade = true;
      })
      .addCase(getUpgradePending.pending, (state) => {
        state.loadingGetUpgradePending = true;
        state.errorGetUpgradePending = false;
      })
      .addCase(getUpgradePending.fulfilled, (state, action) => {
        state.upgradePending = action.payload?.smart_publishing_rules || false;
        state.loadingGetUpgradePending = false;
        state.errorGetUpgradePending = false;
      })
      .addCase(getUpgradePending.rejected, (state) => {
        state.loadingGetUpgradePending = false;
        state.errorGetUpgradePending = true;
      })
      .addCase(addXmlFile.pending, (state) => {
        state.loadingAddXmlFile = true;
        state.errorAddXmlFile = false;
      })
      .addCase(addXmlFile.fulfilled, (state) => {
        state.loadingAddXmlFile = false;
        state.errorAddXmlFile = false;
      })
      .addCase(addXmlFile.rejected, (state) => {
        state.loadingAddXmlFile = false;
        state.errorAddXmlFile = true;
      })
      .addCase(deleteXmlFile.pending, (state) => {
        state.loadingDeleteXmlFile = true;
        state.errorDeleteXmlFile = false;
      })
      .addCase(deleteXmlFile.fulfilled, (state) => {
        state.loadingDeleteXmlFile = false;
        state.errorDeleteXmlFile = false;
      })
      .addCase(deleteXmlFile.rejected, (state) => {
        state.loadingDeleteXmlFile = false;
        state.errorDeleteXmlFile = true;
      })
      .addCase(getXmlFiles.pending, (state) => {
        state.loadingGetXmlFiles = true;
        state.errorGetXmlFiles = false;
      })
      .addCase(getXmlFiles.fulfilled, (state, action) => {
        state.xmlFiles = action.payload;
        state.loadingGetXmlFiles = false;
        state.errorGetXmlFiles = false;
      })
      .addCase(getXmlFiles.rejected, (state) => {
        state.loadingGetXmlFiles = false;
        state.errorGetXmlFiles = true;
      })
      .addCase(getImportedFromOrigins.pending, (state) => {
        state.loadingGetImportedFromOrigins = true;
        state.errorGetImportedFromOrigins = false;
      })
      .addCase(getImportedFromOrigins.fulfilled, (state, action) => {
        state.importedFromOrigins = action.payload;
        state.loadingGetImportedFromOrigins = false;
        state.errorGetImportedFromOrigins = false;
      })
      .addCase(getImportedFromOrigins.rejected, (state) => {
        state.loadingGetImportedFromOrigins = false;
        state.errorGetImportedFromOrigins = true;
      })
      .addCase(getXmlFileHistory.pending, (state) => {
        state.loadingGetXmlFileHistory = true;
        state.errorGetXmlFileHistory = false;
      })
      .addCase(getXmlFileHistory.fulfilled, (state, action) => {
        state.xmlFileHistory = action.payload;
        state.loadingGetXmlFileHistory = false;
        state.errorGetXmlFileHistory = false;
      })
      .addCase(getXmlFileHistory.rejected, (state) => {
        state.loadingGetXmlFileHistory = false;
        state.errorGetXmlFileHistory = true;
      });
  },
});

export const {
  setGeneralConfig,
  updatePublicationLimit,
  setStarringRulesShowInactive,
  setStarringRuleFormData,
  resetStarringRuleForm,
  clearEditingRuleData,
  updateStarringRule,
  setPropsMatchingCriteriaToNone,
  setAttributeValidationError,
  clearAttributeValidationErrors,
  setActiveTab,
  switchToBasicSettings,
  clearXmlFileHistory,
} = PortalSlice.actions;

export const selectPortalData = (state) => state.PortalSlice;

export default PortalSlice.reducer;
